import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

function loadNotes(userId) {
  try {
    const raw = localStorage.getItem(`paper_notes_${userId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveNotes(userId, notes) {
  try {
    localStorage.setItem(`paper_notes_${userId}`, JSON.stringify(notes));
  } catch {}
}

export function usePaperNotes() {
  const { user } = useAuth();
  const userId = user?.id;

  const [notes, setNotes] = useState(() => (userId ? loadNotes(userId) : {}));

  useEffect(() => {
    if (userId) setNotes(loadNotes(userId));
  }, [userId]);

  const saveNote = useCallback(
    (paperId, text) => {
      if (!userId) return;
      setNotes((prev) => {
        const updated = { ...prev, [paperId]: text };
        saveNotes(userId, updated);
        return updated;
      });
    },
    [userId]
  );

  return { notes, saveNote };
}

export default function PaperNote({ paperId, notes, saveNote }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(notes[paperId] || '');

  useEffect(() => {
    setDraft(notes[paperId] || '');
  }, [notes, paperId]);

  const hasNote = Boolean(notes[paperId]);

  const handleSave = () => {
    saveNote(paperId, draft.trim());
    setOpen(false);
  };

  return (
    <div className="mt-1.5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-[#808080] hover:text-[#171717] transition-colors"
        aria-expanded={open}
        aria-label={open ? 'Hide note' : 'Add or edit note'}
      >
        <span>{hasNote ? '📝' : '+'}</span>
        <span>{open ? 'Hide note' : hasNote ? 'View / edit note' : 'Add note'}</span>
      </button>

      {open && (
        <div className="mt-2 flex flex-col gap-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a note about this paper..."
            rows={3}
            className="w-full text-xs rounded-md px-2.5 py-2 text-[#4d4d4d] resize-none focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setDraft(notes[paperId] || '');
                setOpen(false);
              }}
              className="text-xs text-[#808080] hover:text-[#171717] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="text-xs px-3 py-1 bg-[#171717] text-white rounded-md font-medium hover:bg-[#2e2e2e] transition-colors"
            >
              Save note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
